import React, { useState, useMemo } from 'react';
import Select from 'react-select';
import { Skill, Industry } from '@/types/profile';
import { X, Search, Sparkles, Briefcase, Zap, Stars, Lightbulb } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface TagsSectionProps {
  selectedSkills: Skill[];
  selectedIndustries: Industry[];
  onSkillsChange: (skills: Skill[]) => void;
  onIndustriesChange: (industries: Industry[]) => void;
}

// Create categorized skill options
const getCategorizedSkillOptions = () => {
  // Define categories
  const categories = {
    'Programming & Development': [
      'JAVA', 'PYTHON', 'JAVASCRIPT', 'TYPESCRIPT', 'REACT', 'ANGULAR', 
      'VUE', 'NODE_JS', 'SPRING_BOOT', 'DJANGO', 'FLUTTER', 'KOTLIN', 
      'SWIFT', 'C_SHARP', 'PHP'
    ],
    'Data & Analytics': [
      'SQL', 'MONGODB', 'POSTGRESQL', 'DATA_ANALYSIS', 'MACHINE_LEARNING', 'AI'
    ],
    'Design & Creative': [
      'UI_DESIGN', 'UX_DESIGN', 'GRAPHIC_DESIGN', 'ADOBE_SUITE', 'FIGMA', 
      'PHOTOGRAPHY', 'VIDEO_EDITING', 'FILM_MAKING', 'ANIMATION', 'ILLUSTRATION',
      'PAINTING', 'DRAWING', 'SCULPTING'
    ],
    'Marketing & Communication': [
      'SEO', 'SEM', 'SOCIAL_MEDIA_MARKETING', 'CONTENT_MARKETING', 
      'EMAIL_MARKETING', 'CONTENT_WRITING'
    ],
    'Business & Management': [
      'PROJECT_MANAGEMENT', 'AGILE', 'SCRUM', 'BUSINESS_ANALYSIS', 
      'CONSULTING', 'ACCOUNTING', 'LEGAL', 'SALES', 'CUSTOMER_SERVICE', 
      'HR_MANAGEMENT', 'RECRUITING'
    ],
    'Trades & Crafts': [
      'ELECTRICAL', 'PLUMBING', 'CARPENTRY', 'WELDING', 'HVAC', 
      'HOME_RENOVATION', 'HANDYMAN'
    ],
    'Education & Wellness': [
      'TEACHING', 'TUTORING', 'CURRICULUM_DEVELOPMENT', 'LANGUAGE_TEACHING',
      'NURSING', 'PHYSIOTHERAPY', 'MENTAL_HEALTH', 'NUTRITION', 
      'PERSONAL_TRAINING', 'PSYCHOLOGY'
    ],
    'Arts & Entertainment': [
      'MUSIC', 'MUSIC_PRODUCTION'
    ]
  };

  // Create grouped options
  const groupedOptions = Object.entries(categories).map(([groupLabel, skills]) => {
    return {
      label: groupLabel,
      options: skills.map(skill => ({
        value: skill as Skill,
        label: skill.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()),
        category: groupLabel
      }))
    };
  });

  return groupedOptions;
};

// Get all industry options from enum with friendly labels
const getIndustryOptions = () => {
  return Object.values(Industry).map(industry => ({
    value: industry,
    label: industry.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
  }));
};

// Group industries by category
const getGroupedIndustryOptions = () => {
  const techIndustries = ['SOFTWARE_AND_IT', 'DATA_AND_AI', 'CYBERSECURITY', 'GAMING'];
  const businessIndustries = ['CONSULTING', 'FINANCIAL_SERVICES', 'REAL_ESTATE', 'E_COMMERCE', 'LOGISTICS'];
  const mediaIndustries = ['DIGITAL_MEDIA', 'ADVERTISING', 'ENTERTAINMENT', 'DESIGN'];
  const healthIndustries = ['HEALTHCARE', 'MENTAL_HEALTH', 'FITNESS'];
  const serviceIndustries = ['EDUCATION', 'PROFESSIONAL_TRAINING', 'HOSPITALITY', 'RETAIL', 'PERSONAL_SERVICES'];
  const industrialIndustries = ['MANUFACTURING', 'CONSTRUCTION', 'AGRICULTURE', 'ENVIRONMENTAL'];
  const otherIndustries = ['NON_PROFIT', 'OTHER'];

  const categorizedIndustries = [
    {
      label: 'Technology',
      options: techIndustries.map(ind => ({
        value: ind as Industry,
        label: ind.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
      }))
    },
    {
      label: 'Business & Finance',
      options: businessIndustries.map(ind => ({
        value: ind as Industry,
        label: ind.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
      }))
    },
    {
      label: 'Media & Creative',
      options: mediaIndustries.map(ind => ({
        value: ind as Industry,
        label: ind.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
      }))
    },
    {
      label: 'Health & Wellness',
      options: healthIndustries.map(ind => ({
        value: ind as Industry,
        label: ind.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
      }))
    },
    {
      label: 'Services',
      options: serviceIndustries.map(ind => ({
        value: ind as Industry,
        label: ind.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
      }))
    },
    {
      label: 'Industrial & Environmental',
      options: industrialIndustries.map(ind => ({
        value: ind as Industry,
        label: ind.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
      }))
    },
    {
      label: 'Other',
      options: otherIndustries.map(ind => ({
        value: ind as Industry,
        label: ind.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
      }))
    }
  ];

  return categorizedIndustries;
};

// Generate a background color based on the input string
const getTagColor = (text: string) => {
  const colors = [
    'bg-blue-100 text-blue-800 hover:bg-blue-200',
    'bg-green-100 text-green-800 hover:bg-green-200',
    'bg-purple-100 text-purple-800 hover:bg-purple-200',
    'bg-pink-100 text-pink-800 hover:bg-pink-200',
    'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
    'bg-indigo-100 text-indigo-800 hover:bg-indigo-200',
    'bg-red-100 text-red-800 hover:bg-red-200',
    'bg-orange-100 text-orange-800 hover:bg-orange-200',
    'bg-teal-100 text-teal-800 hover:bg-teal-200',
  ];
  
  // Use the string to consistently pick a color from the array
  const hash = text.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

// Custom styles for react-select
const customStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    borderRadius: '0.75rem',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.2)' : 'none',
    borderColor: state.isFocused ? '#4f46e5' : '#e5e7eb',
    '&:hover': { borderColor: state.isFocused ? '#4f46e5' : '#d1d5db' },
    border: '1px solid #e5e7eb',
    padding: '8px',
    minHeight: '54px',
    backgroundColor: '#f9fafb'
  }),
  multiValue: (provided: any) => ({
    ...provided,
    borderRadius: '0.25rem',
    backgroundColor: 'transparent',
    margin: '2px'
  }),
  multiValueLabel: (provided: any) => ({
    ...provided,
    fontSize: '0.875rem',
    color: '#111827',
    padding: '0'
  }),
  multiValueRemove: (provided: any) => ({
    ...provided,
    ':hover': {
      backgroundColor: '#f87171',
      color: 'white'
    }
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: '#9ca3af',
    fontSize: '0.95rem'
  }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: '0.75rem',
    overflow: 'hidden',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
  }),
  groupHeading: (provided: any) => ({
    ...provided,
    fontSize: '0.875rem',
    fontWeight: 'bold',
    color: '#6b7280',
    backgroundColor: '#f9fafb',
    paddingTop: '12px',
    paddingBottom: '12px'
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#f3f4f6' : null,
    color: state.isSelected ? 'white' : '#111827',
    ':active': {
      backgroundColor: state.isSelected ? '#4f46e5' : '#e5e7eb'
    },
    padding: '12px 16px'
  })
};

// Featured skill examples to display
const featuredSkills = [
  { value: 'PYTHON' as Skill, label: 'Python' },
  { value: 'JAVA' as Skill, label: 'Java' },
  { value: 'GRAPHIC_DESIGN' as Skill, label: 'Graphic Design' },
  { value: 'CONTENT_WRITING' as Skill, label: 'Content Writing' },
  { value: 'PROJECT_MANAGEMENT' as Skill, label: 'Project Management' },
  { value: 'PHOTOGRAPHY' as Skill, label: 'Photography' },
];

// Featured industry examples to display
const featuredIndustries = [
  { value: 'SOFTWARE_AND_IT' as Industry, label: 'Software & IT' },
  { value: 'HEALTHCARE' as Industry, label: 'Healthcare' },
  { value: 'FINANCIAL_SERVICES' as Industry, label: 'Financial Services' },
  { value: 'DESIGN' as Industry, label: 'Design' },
  { value: 'ENTERTAINMENT' as Industry, label: 'Entertainment' },
  { value: 'EDUCATION' as Industry, label: 'Education' },
];

const TagsSection: React.FC<TagsSectionProps> = ({
  selectedSkills,
  selectedIndustries,
  onSkillsChange,
  onIndustriesChange,
}) => {
  const [skillsSearchQuery, setSkillsSearchQuery] = useState('');
  const [industriesSearchQuery, setIndustriesSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState("superpowers");
  
  // Get all flattened options for filtering
  const allSkillOptions = useMemo(() => {
    return getCategorizedSkillOptions().flatMap(group => group.options);
  }, []);
  
  const allIndustryOptions = useMemo(() => {
    return getGroupedIndustryOptions().flatMap(group => group.options);
  }, []);

  const handleSkillsChange = (selectedOptions: any) => {
    if (!selectedOptions) {
      onSkillsChange([]);
      return;
    }
    
    // Extract all the selected skill values
    const skills = selectedOptions.map((option: any) => option.value as Skill);
    console.log('Selected skills:', skills);
    
    // Ensure we're passing a valid array of Skill enum values
    // This ensures the skills update is consistent with how industries are updated
    onSkillsChange(skills);
  };

  const handleIndustriesChange = (selectedOptions: any) => {
    if (!selectedOptions) {
      onIndustriesChange([]);
      return;
    }
    const industries = selectedOptions.map((option: any) => option.value as Industry);
    console.log('Selected industries:', industries);
    onIndustriesChange(industries);
  };

  const handleRemoveSkill = (skillToRemove: Skill) => {
    onSkillsChange(selectedSkills.filter(skill => skill !== skillToRemove));
  };

  const handleRemoveIndustry = (industryToRemove: Industry) => {
    onIndustriesChange(selectedIndustries.filter(industry => industry !== industryToRemove));
  };

  const handleQuickAddSkill = (skill: Skill) => {
    if (!selectedSkills.includes(skill)) {
      onSkillsChange([...selectedSkills, skill]);
    }
  };

  const handleQuickAddIndustry = (industry: Industry) => {
    if (!selectedIndustries.includes(industry)) {
      onIndustriesChange([...selectedIndustries, industry]);
    }
  };

  const formatGroupLabel = (data: any) => (
    <div className="flex items-center justify-between">
      <span className="font-medium">{data.label}</span>
      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
        {data.options.length}
      </span>
    </div>
  );

  return (
    <div className="space-y-8">
      <Tabs defaultValue="superpowers" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-2 mb-8">
          <TabsTrigger 
            value="superpowers" 
            className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white py-3"
          >
            <Zap className="h-5 w-5 mr-2" />
            Your Superpowers
          </TabsTrigger>
          <TabsTrigger 
            value="worlds" 
            className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-cyan-600 data-[state=active]:text-white py-3"
          >
            <Lightbulb className="h-5 w-5 mr-2" />
            Your Worlds
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="superpowers" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Stars className="h-6 w-6 text-purple-500" />
                <h3 className="text-xl font-medium text-gray-900">What are your superpowers?</h3>
              </div>
              <p className="text-sm text-gray-600">
                Select the amazing talents and abilities that make you unique. What skills do you love using and sharing with others?
              </p>
              
              {/* Quick-add featured skills */}
              <div className="pt-2 pb-4">
                <div className="flex flex-wrap gap-2">
                  {featuredSkills.map(skill => (
                    <button
                      key={skill.value}
                      type="button"
                      onClick={() => handleQuickAddSkill(skill.value)}
                      disabled={selectedSkills.includes(skill.value)}
                      className={cn(
                        "inline-flex items-center px-4 py-2 rounded-full text-sm transition-all duration-200",
                        selectedSkills.includes(skill.value)
                          ? "bg-purple-100 text-purple-800 border-2 border-purple-300"
                          : "bg-white border-2 border-gray-200 text-gray-800 hover:border-purple-300 hover:bg-purple-50"
                      )}
                    >
                      {selectedSkills.includes(skill.value) && (
                        <span className="mr-1 text-purple-600">✓</span>
                      )}
                      {skill.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Search className="h-4 w-4" />
                </div>
                <Select
                  isMulti
                  name="skills"
                  options={getCategorizedSkillOptions()}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={customStyles}
                  placeholder="Find or add more of your superpowers..."
                  noOptionsMessage={() => "No superpowers found. Try a different search term."}
                  formatGroupLabel={formatGroupLabel}
                  onChange={handleSkillsChange}
                  onInputChange={setSkillsSearchQuery}
                  components={{
                    DropdownIndicator: () => null,
                    MultiValue: () => null
                  }}
                  controlShouldRenderValue={false}
                  value={allSkillOptions.filter(option => 
                    selectedSkills.includes(option.value)
                  )}
                />
              </div>
              
              {selectedSkills.length > 0 && (
                <div className="pt-2">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Your selected superpowers:</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedSkills.map(skill => (
                      <Badge 
                        key={skill} 
                        variant="secondary"
                        className={cn("flex items-center gap-1 py-1.5 px-3 rounded-full cursor-default", getTagColor(skill))}
                      >
                        {skill.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                        <X 
                          className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-red-500 transition-colors"
                          onClick={() => handleRemoveSkill(skill)}
                        />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="worlds" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Briefcase className="h-6 w-6 text-blue-500" />
                <h3 className="text-xl font-medium text-gray-900">Which worlds do you thrive in?</h3>
              </div>
              <p className="text-sm text-gray-600">
                Select the domains and fields where you shine. In what environments and contexts do you feel most at home?
              </p>
              
              {/* Quick-add featured industries */}
              <div className="pt-2 pb-4">
                <div className="flex flex-wrap gap-2">
                  {featuredIndustries.map(industry => (
                    <button
                      key={industry.value}
                      type="button"
                      onClick={() => handleQuickAddIndustry(industry.value)}
                      disabled={selectedIndustries.includes(industry.value)}
                      className={cn(
                        "inline-flex items-center px-4 py-2 rounded-full text-sm transition-all duration-200",
                        selectedIndustries.includes(industry.value)
                          ? "bg-blue-100 text-blue-800 border-2 border-blue-300"
                          : "bg-white border-2 border-gray-200 text-gray-800 hover:border-blue-300 hover:bg-blue-50"
                      )}
                    >
                      {selectedIndustries.includes(industry.value) && (
                        <span className="mr-1 text-blue-600">✓</span>
                      )}
                      {industry.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Search className="h-4 w-4" />
                </div>
                <Select
                  isMulti
                  name="industries"
                  options={getGroupedIndustryOptions()}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={customStyles}
                  placeholder="Find or add more worlds you belong to..."
                  noOptionsMessage={() => "No worlds found. Try a different search term."}
                  formatGroupLabel={formatGroupLabel}
                  onChange={handleIndustriesChange}
                  onInputChange={setIndustriesSearchQuery}
                  components={{
                    DropdownIndicator: () => null,
                    MultiValue: () => null
                  }}
                  controlShouldRenderValue={false}
                  value={allIndustryOptions.filter(option => 
                    selectedIndustries.includes(option.value)
                  )}
                />
              </div>
              
              {selectedIndustries.length > 0 && (
                <div className="pt-2">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Your selected worlds:</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedIndustries.map(industry => (
                      <Badge 
                        key={industry} 
                        variant="secondary"
                        className={cn("flex items-center gap-1 py-1.5 px-3 rounded-full cursor-default", getTagColor(industry))}
                      >
                        {industry.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                        <X 
                          className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-red-500 transition-colors"
                          onClick={() => handleRemoveIndustry(industry)}
                        />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-5 rounded-lg border border-indigo-100">
        <p className="text-sm text-indigo-700">
          <span className="font-medium">💫 Magic tip:</span> Be authentic with your selection! 
          The more genuine you are about your talents and favorite domains, the more meaningful connections you'll make.
        </p>
      </div>
    </div>
  );
};

export default TagsSection; 